import {ForwardedRef, forwardRef, useImperativeHandle, useRef, useState} from 'react';
import type {CSSProperties, ReactNode} from 'react';
import {Button} from '@/components/app/button';
import type {ButtonProps} from '@/components/app/button';
import {Notice} from '@/components/app/feedback';
import {cn} from '@/lib/utils';
import {Controller, useForm, UseFormReturn} from 'react-hook-form';
import type {DefaultValues, FieldPath, FormState} from 'react-hook-form';
import set from 'lodash/set';
import {getErrorMessage} from '../../utils/miscUtils';
import type {FieldObject, FieldObjectsBuilder, FieldValues} from './types';
import {FieldWithType} from './FieldWithType';

import {toastError} from '../../toast';
import {isFetchBaseQueryError} from '../../utils/apiUtils';
export interface FormProps<TFieldValues extends FieldValues> {
  className?: string;
  defaultValues?: DefaultValues<TFieldValues>;
  fields?: FieldObject<TFieldValues>[] | FieldObjectsBuilder<TFieldValues>;
  id?: string;
  onChange?: (fieldValues: TFieldValues) => void;
  onSubmit?: (fieldValues: TFieldValues) => void | Promise<unknown>;
  render?: (options: {
    fieldObjects: FieldObject<TFieldValues>[];
    formState: FormState<TFieldValues>;
    renderError: () => ReactNode;
    renderField: (fieldObject: FieldObject<TFieldValues>) => ReactNode;
    renderFieldByName: (name: FieldPath<TFieldValues>, nth?: number) => ReactNode;
    renderSubmitButton: () => ReactNode;
  }) => ReactNode;
  style?: CSSProperties;
  submitButton?: boolean | ButtonProps;
}
const FormInner = <TFieldValues extends FieldValues>(
  {
    className,
    defaultValues,
    fields = [],
    id,
    onChange,
    onSubmit,
    render,
    style,
    submitButton = true,
  }: FormProps<TFieldValues>,
  ref: ForwardedRef<Form<TFieldValues>>,
) => {
  const form = useRef<HTMLFormElement>(null);
  const useFormReturn = useForm<TFieldValues>({
    defaultValues,
  });
  const {
    control,
    clearErrors,
    formState,
    getFieldState,
    getValues,
    handleSubmit,
    reset,
    resetField,
    setValue,
    trigger,
    watch,
  } = useFormReturn;
  const fieldObjects = Array.isArray(fields)
    ? fields
    : fields({
        clearErrors: (name) => {
          setError(null);
          clearErrors(name);
        },
        formState,
        getFieldState,
        getValues,
        reset: (...args: Parameters<typeof reset>) => {
          setError(null);
          reset(...args);
        },
        resetField,
        setValue,
        submit,
        trigger,
        watch,
      });
  // react-hook-form does not handle errors in onSubmit, so we need to do it ourselves
  const [error, setError] = useState<string | null>(null);
  useImperativeHandle(ref, () => ({
    ...useFormReturn,
    submit,
  }));
  return (
    <form
      className={cn('flex flex-col gap-4', className)}
      id={id}
      noValidate
      onSubmit={handleSubmit(handleSubmitError(onSubmit))}
      ref={form}
      style={style}
    >
      {render == null && (
        <>
          {renderError()}
          {fieldObjects.map((field) => renderField(field))}
          {renderSubmitButton()}
        </>
      )}
      {render != null &&
        render({
          fieldObjects,
          formState,
          renderError,
          renderField,
          renderFieldByName,
          renderSubmitButton,
        })}
    </form>
  );
  function handleSubmitError(onSubmit: FormProps<TFieldValues>['onSubmit']) {
    return (fieldValues: TFieldValues) => {
      const onSubmitReturn = onSubmit?.(fieldValues);
      if (onSubmitReturn instanceof Promise) {
        return onSubmitReturn.catch((e) => {
          if (isFetchBaseQueryError(e) && e.status === 'FETCH_ERROR') {
            toastError(e);
          } else {
            setError(getErrorMessage(e));
          }
        });
      } else {
        return onSubmitReturn;
      }
    };
  }
  function renderError() {
    if (error == null) {
      return null;
    }
    return <Notice className="w-full rounded-3xl rounded-tl-none" tone="error" text={error} />;
  }
  function renderField(fieldObject: FieldObject<TFieldValues>) {
    return (
      <Controller
        control={control}
        defaultValue={fieldObject.defaultValue}
        disabled={fieldObject.disabled}
        key={fieldObject.key ?? fieldObject.name}
        name={fieldObject.name}
        render={(arg) => {
          const modifiedArg = {
            ...arg,
            field: {
              ...arg.field,
              onChange: (value: unknown) => {
                arg.field.onChange(value);
                const changedFieldValues = set(
                  {
                    ...getValues(),
                  },
                  fieldObject.name,
                  value,
                );
                onChange?.(changedFieldValues);
              },
            },
          };
          if ('render' in fieldObject) {
            return fieldObject.render(modifiedArg);
          }
          return <FieldWithType<TFieldValues> {...modifiedArg} fieldObjectWithType={fieldObject} />;
        }}
        rules={fieldObject.rules}
        shouldUnregister={fieldObject.shouldUnregister}
      />
    );
  }
  function renderFieldByName(name: FieldPath<TFieldValues>, nth = 0) {
    const fieldObject = fieldObjects.filter((fieldObject) => fieldObject.name === name)[nth];
    return fieldObject == null ? null : renderField(fieldObject);
  }
  function renderSubmitButton() {
    if (submitButton === false) {
      return null;
    }
    return (
      <Button
        isLoading={formState.isSubmitting}
        {...(typeof submitButton === 'object' ? submitButton : {})}
        form={id}
        type="submit"
      >
        {typeof submitButton === 'object' ? (submitButton.children ?? 'Submit') : 'Submit'}
      </Button>
    );
  }
  function submit() {
    return form.current?.requestSubmit();
  }
};
export const Form = forwardRef(FormInner) as <TFieldValues extends FieldValues>(
  props: FormProps<TFieldValues> & {
    ref?: ForwardedRef<Form<TFieldValues>>;
  },
) => ReturnType<typeof FormInner>;
export type Form<TFieldValues extends FieldValues = FieldValues> = UseFormReturn<TFieldValues> & {
  submit: () => void;
};
